'use client';
import { FC, PropsWithChildren, useMemo } from 'react';

import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { Breakpoints } from '@mui/material';
import {
  createTheme,
  Shadows,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { createSpacing } from '@mui/system';
import { SpacingOptions } from '@mui/system/createTheme/createSpacing';
import { DesignsToken } from '@repo/tokens-studio';

import { buildTypography } from './buildTypography';

type SpacingArgument = keyof DesignsToken['spacing'] | 0;
interface Spacing {
  (): string;
  (value: SpacingArgument): string;
  (topBottom: SpacingArgument, rightLeft: SpacingArgument): string;
  (
    top: SpacingArgument,
    rightLeft: SpacingArgument,
    bottom: SpacingArgument,
  ): string;
  (
    top: SpacingArgument,
    right: SpacingArgument,
    bottom: SpacingArgument,
    left: SpacingArgument,
  ): string;
}

declare module '@emotion/react' {
  export interface Theme extends Omit<DesignsToken, 'spacing'> {
    spacing: Spacing;
    breakpoints: Breakpoints;
  }
}

export const ThemeDispatcher: FC<
  PropsWithChildren<{ theme: DesignsToken }>
> = ({ theme, children }) => {
  // Extract typography, shape to not overwrite designs token theme values
  const {
    typography: muiTypography,
    shape: muiShape,
    ...muiTheme
  } = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: theme.yellow['50'],
          },
          secondary: {
            main: theme.blue['50'],
          },
          error: {
            main: theme.red['50'],
          },
          success: {
            main: theme.green['50'],
          },
        },
        spacing: createSpacing(((value: SpacingArgument) =>
          value === 0 ? 0 : theme.spacing[value]) as SpacingOptions),
        shadows: [
          'none',
          theme.elevation.level0,
          theme.elevation.level1,
          theme.elevation.level2,
          theme.elevation.level3,
          theme.elevation.level4,
          ...Array.from({ length: 19 }, () => theme.elevation.level5),
        ] as Shadows,
      }),
    [theme],
  );

  const typographies = buildTypography(theme);
  // Attach mui utility methods for mui compatibility
  (typographies.typography as any).pxToRem = muiTypography.pxToRem;

  return (
    <EmotionThemeProvider
      theme={{
        ...theme,
        ...typographies,
        spacing: muiTheme.spacing,
        breakpoints: muiTheme.breakpoints,
      }}
    >
      <MuiThemeProvider
        theme={{ ...muiTheme, typography: typographies.typography }}
      >
        {children}
      </MuiThemeProvider>
    </EmotionThemeProvider>
  );
};
